import type { ReactNode } from 'react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { StepArrow } from './AttributionParts'

/**
 * The step strip under the tables (Paper "From ad to payment", "From visit to
 * payment"): label, one number, one note per step, and the step rate over a
 * thin arrow between them. All ink; `warn` is the one drop that needs action.
 * A `placeholder` step is the dashed "Needs Stripe" box.
 */

export type FunnelStep = {
  label: string
  value: string
  note?: string | null
  /** faint: secondary detail; muted: worth reading; ink: the money line */
  noteTone?: 'faint' | 'muted' | 'ink'
  warn?: boolean
  extra?: ReactNode
}

export type FunnelPlaceholder = {
  placeholder: true
  label: string
  title: string
  body: string
}

export type FunnelItem = (FunnelStep | FunnelPlaceholder) & {
  /** Rate from the previous step, shown over the arrow; null hides it */
  rateIn?: string | null
}

const NOTE_TONE = {
  faint: 'text-ui-meta',
  muted: 'text-muted-foreground',
  ink: 'font-medium text-ui-ink',
}

function Arrow({ rate }: { rate: string | null | undefined }) {
  return (
    <div className="hidden w-[72px] shrink-0 flex-col items-center justify-center gap-1 md:flex" aria-hidden>
      {rate && <span className="text-xs font-medium leading-4 text-ui-ink tabular-nums">{rate}</span>}
      <StepArrow />
    </div>
  )
}

/** The steps row alone, for cards that bring their own header. */
export function FunnelSteps({ items }: { items: FunnelItem[] }) {
  return (
    <div className="flex flex-wrap gap-y-4 px-5 pt-3.5 pb-[18px] md:flex-nowrap">
      {items.map((item, i) => (
        <div key={item.label} className="contents">
          {i > 0 && <Arrow rate={item.rateIn} />}
          {'placeholder' in item ? (
            <div className="flex min-w-[200px] grow-[1.6] basis-0 flex-col gap-1 rounded-[10px] border border-dashed border-input bg-muted/60 px-3.5 py-2.5">
              <span className="text-xs leading-4 text-muted-foreground">{item.label}</span>
              <span className="text-sm font-medium leading-[18px] text-foreground">{item.title}</span>
              <span className="text-xs leading-4 text-muted-foreground">{item.body}</span>
            </div>
          ) : (
            <div className="flex min-w-[45%] flex-1 basis-0 flex-col gap-1 md:min-w-0">
              <span className="text-xs leading-4 text-muted-foreground">{item.label}</span>
              <span className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums">
                {item.value}
              </span>
              {item.note && (
                <span className={cn('text-xs leading-4', item.warn ? 'text-warning' : NOTE_TONE[item.noteTone ?? 'faint'])}>
                  {item.note}
                </span>
              )}
              {item.extra}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default function FunnelStrip({
  title,
  subtitle,
  items,
}: {
  title: string
  subtitle?: string | null
  items: FunnelItem[]
}) {
  return (
    <Card className="gap-0 py-0">
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 px-5 pt-4 pb-1">
        <h3 className="text-sm font-medium leading-[18px] text-foreground">{title}</h3>
        {subtitle && <span className="text-[13px] leading-4 text-ui-secondary">{subtitle}</span>}
      </div>
      <FunnelSteps items={items} />
    </Card>
  )
}
